import { stringToBase64 } from "@entrilia/shared/utilities/stringHelper";
import {
  combineValidators,
  fileToUploadValidator,
  invalidResult,
  maxCharactersValidator,
  nonEmptyArrayValidator,
  requiredValidator,
  uniqueValidator,
  validResult,
  websiteUrlValidator,
} from "@entrilia/shared/utilities/validators";
import type {
  AddVideoRequest,
  VideoLibraryCategory,
  VideoLibraryUploadTarget,
} from "../../../api/types/videoLibraryTypes";
import type { FileUploadParams } from "./fileUploaderService";

interface AddVideoForm {
  sourceType: VideoSourceType;
  target: VideoLibraryUploadTarget | "";
  videoLink: string;
  title: string;
  categories: VideoLibraryCategory[];
  uploadedAt: Date | null;
  description: string;
  touchedFields: Set<ValidationFieldKey>;
  file: File | undefined;
  invalidFileError: string;
}

export type VideoSourceType = "link" | "file";

const validationFieldKeys = ["videoLink", "title", "categories", "file"] as const;
type ValidationFieldKey = (typeof validationFieldKeys)[number];

export const getInitialAddVideoForm = (): AddVideoForm => ({
  sourceType: "link",
  target: "",
  videoLink: "",
  title: "",
  categories: [],
  uploadedAt: new Date(),
  description: "",
  touchedFields: new Set<ValidationFieldKey>(),
  file: undefined,
  invalidFileError: "",
});

export const mapFormToAddRequest = (form: AddVideoForm): AddVideoRequest | undefined => {
  return form.target
    ? {
        target: form.target,
        videoLinkEncoded: stringToBase64(form.videoLink),
        title: form.title.trim(),
        descriptionUserInput: form.description.trim(),
        categoryIds: form.categories.map((category) => category.id),
        uploadedAt: form.uploadedAt ? form.uploadedAt.toISOString() : undefined,
      }
    : undefined;
};

export const mapFormToFileUploadParams = (form: AddVideoForm): FileUploadParams | undefined =>
  form.file !== undefined
    ? {
        title: form.title,
        description: form.description,
        categoryIds: form.categories.map((category) => category.id),
        file: form.file,
        duration: 0, // will be assigned later in the component
        uploadedAt: form.uploadedAt ? form.uploadedAt.toISOString() : undefined,
      }
    : undefined;

interface FormValidationResult {
  isFormValid: boolean;
  validationErrors: Partial<Record<ValidationFieldKey, string>>;
}

export const acceptedVideoFileExtensions = [".mp4", ".webm", ".ogv", ".mov"];

export const maxVideoFileSize = 1_000_000_000; // 1 GB

const validateTarget = requiredValidator;
const validateSharePointVideoLink = combineValidators(
  requiredValidator,
  websiteUrlValidator(["entrilia-my.sharepoint.com"])
);
const validateVimeoVideoLink = combineValidators(requiredValidator, websiteUrlValidator(["player.vimeo.com"]));

const validateCategories = nonEmptyArrayValidator("At least one category is required");
const validateFile = fileToUploadValidator({
  maxFileSize: maxVideoFileSize,
  acceptedFileExtensions: acceptedVideoFileExtensions,
});

export const validateForm = (form: AddVideoForm, existingVideoNames: string[]): FormValidationResult => {
  const validationErrors: Partial<Record<ValidationFieldKey, string>> = {};

  const validateTitle = combineValidators(
    requiredValidator,
    maxCharactersValidator(100),
    uniqueValidator("A video with such title already exists", existingVideoNames)
  );

  const targetResult = form.sourceType === "link" ? validateTarget(form.target) : validResult();
  const videoLinkResult =
    form.sourceType === "link"
      ? form.target === "SharePoint"
        ? validateSharePointVideoLink(form.videoLink)
        : form.target === "DirectLink"
          ? validateVimeoVideoLink(form.videoLink)
          : validResult()
      : validResult();
  const titleResult = validateTitle(form.title);
  const categoriesResult = validateCategories(form.categories);
  const fileResult =
    form.sourceType === "file"
      ? form.file !== undefined
        ? validateFile(form.file)
        : invalidResult("File is required")
      : validResult();

  const isFormValid =
    targetResult.isValid &&
    videoLinkResult.isValid &&
    titleResult.isValid &&
    categoriesResult.isValid &&
    fileResult.isValid;

  validationErrors.videoLink = videoLinkResult.error;
  validationErrors.title = titleResult.error;
  validationErrors.categories = categoriesResult.error;
  validationErrors.file = fileResult.error;

  validationFieldKeys.forEach((key) => {
    if (!form.touchedFields.has(key)) {
      validationErrors[key] = "";
    }
  });

  return {
    isFormValid,
    validationErrors,
  };
};

export const determineDurationFromFile = (file: File, videoElement: HTMLVideoElement): Promise<number | undefined> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);

    videoElement.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(videoElement.duration);
    };

    videoElement.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(undefined);
    };

    videoElement.src = url;
  });
};

export const determineDurationFromUrl = (url: string, videoElement: HTMLVideoElement): Promise<number | undefined> => {
  return new Promise((resolve) => {
    videoElement.onloadedmetadata = () => {
      resolve(videoElement.duration);
    };

    videoElement.onerror = () => {
      resolve(undefined);
    };

    videoElement.src = url;
  });
};
